/**
 * Compliance API — contribution limits, expenditures, and FEC-style filing.
 *
 * Thin typed wrappers over the backend `/compliance/*` router. Follows the
 * repo convention: axios via the shared default `client`, plain async fns,
 * blob response for the CSV export.
 *
 * Backend role gates (enforced server-side; the view mirrors them as
 * defense-in-depth):
 *   - reads (rules list, limits, disclaimer, expenditures list) → analyst+
 *   - writes (upsert rule, create expenditure)                  → admin/org_manager
 *   - expenditures CSV export                                   → admin/org_manager/manager
 */
import client from '@/api/client';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/** A contribution-limit rule for an office (optionally scoped to a jurisdiction). */
export interface ContributionRule {
  id: number;
  office_type: string;
  jurisdiction: string | null;
  /** null = no limit (unlimited). */
  limit_cents: number | null;
}

/** Payload for POST /compliance/rules (upsert on office_type + jurisdiction). */
export interface ContributionRuleInput {
  office_type: string;
  jurisdiction?: string | null;
  /** null = unlimited. */
  limit_cents: number | null;
}

/** Resolved limit for an office lookup. */
export interface LimitResult {
  office_type: string;
  jurisdiction: string | null;
  limit_cents: number | null;
  unlimited: boolean;
}

/** A recorded campaign expenditure with its auto-generated disclaimer. */
export interface Expenditure {
  id: number;
  payer: string;
  disclaimer: string;
  channel: string | null;
  office_type: string | null;
  contest_label: string | null;
  amount_cents: number;
  external_ref: string | null;
  spent_at: string | null;
  created_at: string;
}

/** Payload for POST /compliance/expenditures (disclaimer auto-generated server-side). */
export interface ExpenditureInput {
  payer: string;
  amount_cents: number;
  channel?: string | null;
  office_type?: string | null;
  contest_label?: string | null;
  external_ref?: string | null;
  spent_at?: string | null;
}

/** Normalized result of listing expenditures. */
export interface ExpenditureList {
  items: Expenditure[];
  total_cents: number;
}

// ---------------------------------------------------------------------------
// Reads (analyst+)
// ---------------------------------------------------------------------------

/** GET /compliance/rules → all contribution-limit rules. */
export async function listRules(): Promise<ContributionRule[]> {
  const res = await client.get<ContributionRule[]>('/compliance/rules');
  return Array.isArray(res.data) ? res.data : [];
}

/** GET /compliance/limits?office_type=... → the resolved limit for an office. */
export async function getLimit(officeType: string): Promise<LimitResult> {
  const res = await client.get<LimitResult>('/compliance/limits', {
    params: { office_type: officeType },
  });
  return res.data;
}

/** GET /compliance/disclaimer?payer=... → the "Paid for by <payer>." string. */
export async function getDisclaimer(payer: string): Promise<string> {
  const res = await client.get<{ disclaimer: string }>('/compliance/disclaimer', {
    params: { payer },
  });
  return res.data?.disclaimer ?? '';
}

/**
 * GET /compliance/expenditures → recorded expenditures + total.
 *
 * The backend shape may be either `{ items, total_cents }` or a bare array;
 * both are handled defensively so a shape drift never crashes the view. When
 * only a bare array arrives, the total is summed client-side.
 */
export async function listExpenditures(): Promise<ExpenditureList> {
  const res = await client.get<ExpenditureList | Expenditure[]>('/compliance/expenditures');
  const data = res.data;
  if (Array.isArray(data)) {
    const items = data;
    const total_cents = items.reduce((sum, e) => sum + (e.amount_cents ?? 0), 0);
    return { items, total_cents };
  }
  const items = Array.isArray(data?.items) ? data.items : [];
  const total_cents =
    typeof data?.total_cents === 'number'
      ? data.total_cents
      : items.reduce((sum, e) => sum + (e.amount_cents ?? 0), 0);
  return { items, total_cents };
}

// ---------------------------------------------------------------------------
// Writes (admin/org_manager)
// ---------------------------------------------------------------------------

/** POST /compliance/rules → upsert a contribution-limit rule. */
export async function upsertRule(rule: ContributionRuleInput): Promise<ContributionRule> {
  const res = await client.post<ContributionRule>('/compliance/rules', rule);
  return res.data;
}

/** POST /compliance/expenditures → record an expenditure (disclaimer auto-generated). */
export async function createExpenditure(body: ExpenditureInput): Promise<Expenditure> {
  const res = await client.post<Expenditure>('/compliance/expenditures', body);
  return res.data;
}

// ---------------------------------------------------------------------------
// Export (admin/org_manager/manager)
// ---------------------------------------------------------------------------

/** GET /compliance/expenditures/export → CSV filing as a Blob. */
export async function exportExpendituresCsv(): Promise<Blob> {
  const res = await client.get('/compliance/expenditures/export', {
    responseType: 'blob',
  });
  return res.data;
}
